import { inject, Injectable } from '@angular/core';
import { Observable, of, map, switchMap, catchError, shareReplay, forkJoin } from 'rxjs';
import { Api } from 'bimplus-websdk';
import { BIMPLUS_API } from '@web-portal/domains/shared/bimplus-core';
import type {
  Member,
  MemberDto,
  MemberRole,
  MemberStatus,
  InvitationDto,
} from './member.interface';
export interface InviteResult {
  email: string;
  success: boolean;
  alreadyMember: boolean;
}

interface ProjectPrerequisites {
  adminRoleId: string;
  generalGroupId: string | null;
  members: Member[];
}

/**
 * MembersAdapterService - Adapter over bimplus-websdk for project members API.
 *
 * Handles fetching and managing project members, as well as invitations.
 *
 * Follows the BIM Portal's two-path approach for invitations:
 * 1. Check if user exists via `api.users.exist()`
 * 2. If exists → add to team first, then `api.projects.postMember()` (add to project)
 * 3. If not → `api.invitations.post()` (send invitation email)
 */
@Injectable({
  providedIn: 'root',
})
export class MembersAdapterService {
  private readonly api: Api = inject(BIMPLUS_API);
  private roles$?: Observable<{ id: string; name: string }[]>;
  private readonly projectGroups = new Map<string, Observable<{ id: string }[]>>();

  /**
   * Fetches and caches the list of roles from the API.
   */
  private getRoles(): Observable<{ id: string; name: string }[]> {
    this.roles$ ??= this.api.rxRequest(this.api.roles.get()).pipe(shareReplay(1));
    return this.roles$;
  }

  /**
   * Returns the role ID for 'Project_Admin'.
   */
  private getAdminRoleId(): Observable<string> {
    return this.getRoles().pipe(
      map(roles => {
        const admin = roles.find(r => r.name === 'Project_Admin');
        if (!admin) throw new Error('Project_Admin role not found');
        return admin.id;
      })
    );
  }

  /**
   * Fetches and caches user groups for a project.
   */
  private getProjectGroups(projectId: string): Observable<{ id: string }[]> {
    if (!this.projectGroups.has(projectId)) {
      const url = `${this.api.getApiTeamUrl()}projects/${projectId}/usergroups`;
      this.projectGroups.set(
        projectId,
        this.api.rxRequest(this.api.request.get<{ id: string }[]>(url)).pipe(shareReplay(1))
      );
    }
    return this.projectGroups.get(projectId)!;
  }

  /**
   * Retrieves all members and pending invitations for a project.
   *
   * Merges actual project members with pending invitations (users who
   * have been invited but haven't accepted yet), mirroring the BIM Portal approach.
   */
  getMembers(projectId: string): Observable<Member[]> {
    const membersUrl = `${this.api.getApiTeamUrl()}projects/${projectId}/members`;
    const members$ = this.api
      .rxRequest(this.api.request.get<MemberDto[]>(membersUrl))
      .pipe(map(dtos => dtos.map(dto => this.mapToMember(dto))));

    const invitations$ = this.api
      .rxRequest(this.api.invitations.getProjectInvitations(projectId) as Promise<InvitationDto[]>)
      .pipe(
        map(invitations => invitations.map(inv => this.mapInvitationToMember(inv))),
        catchError(() => of([] as Member[]))
      );

    return forkJoin([members$, invitations$]).pipe(
      map(([members, invitations]) => [...members, ...invitations])
    );
  }

  /**
   * Checks whether a user with the given email already has an account.
   *
   * Note: The bimplus-websdk does not expose HTTP status codes, so we cannot
   * distinguish a 404 (user not found) from a network/server error. All errors
   * are mapped to `null` (treated as "user does not exist"), which matches the
   * BIM Portal's original implementation.
   *
   * @returns Observable of user object if exists, null if not found.
   */
  checkUserExists(email: string): Observable<{ id: string } | null> {
    return this.api
      .rxRequest(this.api.users.exist(encodeURIComponent(email)))
      .pipe(catchError(() => of(null)));
  }

  /**
   * Fetches all data needed for inviting users to a project in a single batch:
   * admin role ID, general group ID, and current members + pending invitations.
   */
  private getProjectPrerequisites(projectId: string): Observable<ProjectPrerequisites> {
    return forkJoin([
      this.getAdminRoleId(),
      this.getProjectGroups(projectId),
      this.getMembers(projectId),
    ]).pipe(
      map(([adminRoleId, groups, members]) => ({
        adminRoleId,
        generalGroupId: groups[0]?.id ?? null,
        members,
      }))
    );
  }

  /**
   * Invites multiple users to a project in a single batch.
   *
   * Fetches prerequisites (role, group, current members) once, then processes
   * all emails in parallel. Each email is checked against existing members and
   * pending invitations before sending the API call.
   *
   * @returns Observable of results per email with success/failure/already-member status.
   */
  inviteMultipleToProject(projectId: string, emails: string[]): Observable<InviteResult[]> {
    console.debug('[MembersAdapter] inviteMultipleToProject:start', {
      projectId,
      emails,
      count: emails.length,
    });

    return this.getProjectPrerequisites(projectId).pipe(
      switchMap(({ adminRoleId, generalGroupId, members }) => {
        console.debug('[MembersAdapter] inviteMultipleToProject:prerequisites', {
          projectId,
          adminRoleId,
          generalGroupId,
          membersCount: members.length,
        });

        const memberEmails = new Set(members.map(m => m.email.toLowerCase()));
        const invitations$ = emails.map(email =>
          this.processEmailInvitation(email, projectId, adminRoleId, generalGroupId, memberEmails)
        );
        return forkJoin(invitations$);
      }),
      map(results => {
        console.debug('[MembersAdapter] inviteMultipleToProject:results', {
          projectId,
          results,
        });
        return results;
      })
    );
  }

  private processEmailInvitation(
    email: string,
    projectId: string,
    adminRoleId: string,
    generalGroupId: string | null,
    memberEmails: Set<string>
  ): Observable<InviteResult> {
    if (memberEmails.has(email.toLowerCase())) {
      console.debug('[MembersAdapter] processEmailInvitation:already-member', {
        projectId,
        email,
      });
      return of({ email, success: false, alreadyMember: true });
    }

    return this.checkUserExists(email).pipe(
      switchMap(user => {
        if (user?.id) {
          console.debug('[MembersAdapter] processEmailInvitation:existing-user', {
            projectId,
            email,
            userId: user.id,
          });
          return this.addExistingUserViaTeam(projectId, user.id, adminRoleId, generalGroupId);
        }

        console.debug('[MembersAdapter] processEmailInvitation:new-invitation', {
          projectId,
          email,
        });
        return this.createInvitation(projectId, email, adminRoleId, generalGroupId);
      }),
      map((): InviteResult => {
        console.debug('[MembersAdapter] processEmailInvitation:success', {
          projectId,
          email,
        });
        return { email, success: true, alreadyMember: false };
      }),
      catchError(error => {
        console.error('[MembersAdapter] processEmailInvitation:failed', {
          projectId,
          email,
          error,
        });
        return of<InviteResult>({ email, success: false, alreadyMember: false });
      })
    );
  }

  private addExistingUserViaTeam(
    projectId: string,
    userId: string,
    adminRoleId: string,
    generalGroupId: string | null
  ): Observable<unknown> {
    return this.addUserToTeam(userId).pipe(
      catchError(() => of(null)),
      switchMap(() => this.addExistingUserToProject(projectId, userId, adminRoleId, generalGroupId))
    );
  }

  /**
   * Removes an active member from a project.
   */
  removeMember(projectId: string, memberId: string): Observable<unknown> {
    return this.api.rxRequest(
      this.api.projects.deleteMember(projectId, JSON.stringify({ member: { id: memberId } }))
    );
  }

  /**
   * Revokes (deletes) a pending invitation.
   */
  revokeInvitation(invitationId: string): Observable<unknown> {
    return this.api.rxRequest(this.api.invitations.delete(invitationId));
  }

  /**
   * Resends a pending invitation by updating it via PUT.
   *
   * This re-triggers the invitation email with the same role and group assignment.
   */
  resendInvitation(projectId: string, invitationId: string, email: string): Observable<unknown> {
    return forkJoin([this.getAdminRoleId(), this.getProjectGroups(projectId)]).pipe(
      switchMap(([adminRoleId, groups]) =>
        this.api.rxRequest(
          this.api.invitations.put(
            invitationId,
            JSON.stringify({
              email,
              projects: [
                {
                  projectId,
                  roleId: adminRoleId,
                  group: groups[0] ? { id: groups[0].id } : undefined,
                },
              ],
            })
          )
        )
      )
    );
  }

  /**
   * Maps a MemberDto from the API to a Member domain object.
   *
   * @param dto - The raw DTO from the API.
   * @returns The mapped Member object.
   */
  private mapToMember(dto: MemberDto): Member {
    const memberInfo = dto.member;
    return {
      id: memberInfo?.id ?? '',
      name: memberInfo?.fullname ?? memberInfo?.displayname ?? memberInfo?.email ?? '',
      email: memberInfo?.email ?? '',
      role: this.mapRole(dto.role?.name),
      isOwner: dto.role?.name === 'Account_Owner',
      status: this.mapStatus(memberInfo?.status),
    };
  }

  /**
   * Maps a role name from the API to a MemberRole type.
   */
  private mapRole(roleName?: string): MemberRole {
    const normalized = roleName?.toLowerCase() ?? '';
    if (normalized.includes('admin') || normalized.includes('owner')) {
      return 'Admin';
    }
    if (normalized.includes('editor') || normalized.includes('write')) {
      return 'Editor';
    }
    return 'Viewer';
  }

  /**
   * Maps a status string from the API to a MemberStatus type.
   */
  private mapStatus(status?: string): MemberStatus {
    switch (status?.toLowerCase()) {
      case 'active':
        return 'Active';
      case 'pending':
        return 'Pending';
      case 'inactive':
        return 'Inactive';
      default:
        return 'Pending';
    }
  }

  private mapInvitationToMember(invitation: InvitationDto): Member {
    const name =
      invitation.displayname ??
      ([invitation.firstname, invitation.lastname].filter(Boolean).join(' ') || invitation.email);
    return {
      id: invitation.id,
      name,
      email: invitation.email,
      role: 'Admin',
      status: 'Pending',
    };
  }

  /**
   * Adds a user to the team before adding them to a project.
   *
   * Mirrors the BIM Portal's two-step flow: team membership is a prerequisite
   * for project membership. If the user is already a team member, the API
   * returns a 403 which is caught and swallowed by the caller.
   */
  private addUserToTeam(userId: string): Observable<unknown> {
    const url = `${this.api.getApiTeamUrl()}members`;
    return this.api.rxRequest(
      this.api.request.post(url, JSON.stringify({ user: { id: userId }, role: 'Member' }), {})
    );
  }

  private addExistingUserToProject(
    projectId: string,
    userId: string,
    adminRoleId: string,
    generalGroupId: string | null
  ): Observable<unknown> {
    return this.api.rxRequest(
      this.api.projects.postMember(
        projectId,
        JSON.stringify({
          member: { id: userId },
          role: { id: adminRoleId },
          groups: generalGroupId ? [{ id: generalGroupId }] : [],
        })
      )
    );
  }

  private createInvitation(
    projectId: string,
    email: string,
    adminRoleId: string,
    generalGroupId: string | null
  ): Observable<unknown> {
    return this.api.rxRequest(
      this.api.invitations.post(
        JSON.stringify({
          email,
          projects: [
            {
              projectId,
              roleId: adminRoleId,
              group: generalGroupId ? { id: generalGroupId } : undefined,
            },
          ],
        })
      )
    );
  }
}
